import { useId } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import type {
    ClarifyCatalog,
    PenRef,
    ProviderGroup,
    ProviderSchema,
    SecretName,
} from '@/domain/providerConfig';
import { secretFieldsOf } from '@/routes/shared/ProviderConfig/schemaFields';

import type { FieldConflict } from '../form/conflictMerge';
import type { SecretOutlook } from '../form/effectiveRows';
import { formFields, type ProviderFormValues } from '../form/formModel';

import { PenMappingRow } from './PenMappingRow';

import rowStyles from './penRow.module.scss';
import styles from './sections.module.scss';

export interface PenMappingTableProps {
    schema: ProviderSchema;
    group: ProviderGroup | null;
    pens: PenRef[];
    control: Control<ProviderFormValues>;
    setValue: UseFormSetValue<ProviderFormValues>;
    isOptoScaleAdmin: boolean;
    catalog: ClarifyCatalog | null;
    /** What the last listing came to, next to its button: see CatalogLoader['message']. */
    catalogMessage?: string | null;
    onLoadCatalog: () => void;
    onFetchHistory: (pen: PenRef) => void;
    conflicts: FieldConflict[];
    /** By pen: what each secret box should say while left as it is (penSecretOutlook). */
    secretOutlook: ReadonlyMap<number, Readonly<Record<SecretName, SecretOutlook>>>;
}

/** Every pen of the site, connected to this provider or not. */
function PenMappingTable({
    schema,
    group,
    pens,
    control,
    setValue,
    isOptoScaleAdmin,
    catalog,
    catalogMessage = null,
    onLoadCatalog,
    onFetchHistory,
    conflicts,
    secretOutlook,
}: PenMappingTableProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const fields = formFields(schema, { includeAdvanced: isOptoScaleAdmin }).filter(
        (field) => field.scope === 'pen'
    );
    const secrets = secretFieldsOf(schema);
    const usesCatalog = fields.some(
        (field) => field.kind === 'clarifyItem' || field.kind === 'scadaItems'
    );
    const headingId = `${id}-heading`;

    return (
        <section className={styles.section} aria-labelledby={headingId}>
            <div className={styles.sectionHeader}>
                <h3 id={headingId} className={styles.heading}>
                    {t('providerConfig.sections.pens')}
                </h3>
                {usesCatalog && (
                    <Button
                        variant={catalog === null ? 'secondary' : 'tertiary'}
                        onClick={onLoadCatalog}>
                        {t(
                            catalog === null
                                ? 'providerConfig.pens.loadCatalog'
                                : 'providerConfig.pens.reloadCatalog'
                        )}
                    </Button>
                )}
            </div>
            {/* Always mounted: a live region added together with its text is not announced. */}
            <div aria-live="polite">
                {catalogMessage !== null && (
                    <Alert tone="warning" size="small" live="off">
                        {t(catalogMessage)}
                    </Alert>
                )}
            </div>
            {catalog?.truncated && (
                <p className={styles.note}>{t('providerConfig.pens.catalogTruncated')}</p>
            )}
            <ul className={rowStyles.penList}>
                {pens.map((pen) => (
                    <PenMappingRow
                        key={pen.cageId}
                        pen={pen}
                        row={group?.rows.find((row) => row.cageId === pen.cageId) ?? null}
                        group={group}
                        fields={fields}
                        secrets={secrets.map((secret) => ({
                            ...secret,
                            outlook: secretOutlook.get(pen.cageId)?.[secret.name],
                        }))}
                        control={control}
                        setValue={setValue}
                        isOptoScaleAdmin={isOptoScaleAdmin}
                        catalog={catalog}
                        onFetchHistory={onFetchHistory}
                        conflicts={conflicts}
                    />
                ))}
            </ul>
        </section>
    );
}

export default PenMappingTable;
export { PenMappingTable };
